/**
 * RepartoFactura — Repartir UNA factura entre VARIAS OT (D11, 1-sep-2026).
 *
 * El mecanismo del reparto existe en la base desde agosto (`Factura.repartos`) y el cuadre
 * ya lo consume, pero **ninguna factura lo usaba**: no había por dónde cargarlo. La
 * pantalla Cuadre mostraba el descuadre y no ofrecía manera de arreglarlo — había que
 * anotarse el folio e irlo a buscar a otra pantalla.
 *
 * De ahí salen los descuadres del bloque Rocmin: facturas que consolidan varias guías de
 * despacho y a las que se les atribuyó una sola OT, apareciendo como sobre-vinculadas por
 * millones que en realidad corresponden a otras entregas.
 *
 * DOS COSAS QUE ESTA PANTALLA NO HACE, a propósito:
 *
 *   · No propone un reparto. El contador demostró que deducirlo por aritmética es
 *     imposible —hay entre 6 y 102 combinaciones que suman exacto por factura, y las
 *     cuatro facturas exceden en $8,7 millones al universo de OT candidatas—. Sugerir una
 *     combinación "que calza" sería darle apariencia de dato a una coincidencia.
 *   · No completa el resto automáticamente. Si sobra plata sin asignar lo dice y deja
 *     guardar: una factura puede cubrir algo que no es una OT. Lo que no puede es que
 *     sobre en silencio.
 */
import React, { useEffect, useState } from "react";
import backend from "../../backend";

const clp = n => `$${Math.round(Number(n) || 0).toLocaleString("es-CL")}`;

const campo = {
  background: "#111", border: "1px solid #2e2e2e", borderRadius: 5,
  padding: "5px 8px", color: "#ddd", fontSize: 12, boxSizing: "border-box",
};

const RepartoFactura = ({ documento, otActual, onListo, onCancelar }) => {
  const [lineas, setLineas] = useState([]);
  const [ots, setOts] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");

  const neto = Number(documento?.netoDocumento) || 0;

  useEffect(() => {
    let vivo = true;
    // La OT desde la que se abrió arranca cargada con lo que hoy tiene atribuido: es el
    // punto de partida natural y evita retipear lo que ya estaba.
    setLineas([{ workOrderId: otActual?.id || "", numero: otActual?.numero || "", monto: "" }]);
    backend.get("workOrder?limit=400")
      .then(({ data }) => {
        if (!vivo) return;
        const lista = Array.isArray(data) ? data : (data?.workOrders || data?.items || []);
        setOts(lista.map(w => ({ id: w._id, numero: w.number, nombre: w.name, total: w.total })));
      })
      .catch(() => {});
    return () => { vivo = false; };
  }, [documento?._id, otActual?.id, otActual?.numero]);

  const set = (i, campoNombre, valor) =>
    setLineas(ls => ls.map((l, k) => (k === i ? { ...l, [campoNombre]: valor } : l)));

  const suma = lineas.reduce((s, l) => s + (Number(l.monto) || 0), 0);
  const resto = neto - suma;
  const excede = suma > neto + 10;

  const guardar = async () => {
    const utiles = lineas.filter(l => l.workOrderId && Number(l.monto) > 0);
    if (!utiles.length) { setError("Poné al menos una OT con monto"); return; }
    if (excede) { setError("El reparto supera el neto de la factura"); return; }
    setGuardando(true);
    setError("");
    try {
      const { data } = await backend.patch(`sii/facturas/${documento._id}/vincular`, {
        repartos: utiles.map(l => ({ workOrderId: l.workOrderId, monto: Math.round(Number(l.monto)) })),
      });
      if (data?.avisoReparto) setAviso(data.avisoReparto);
      onListo?.(data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={{ background: "#111", border: "1px solid #2e2e2e", borderRadius: 6,
                  padding: 12, marginTop: 8 }}>
      <div style={{ fontSize: 11, color: "#cbd5e1", marginBottom: 2 }}>
        Repartir {documento.tipoDoc === 61 ? "NC" : "F"}{documento.folio} · neto {clp(neto)}
      </div>
      <div style={{ fontSize: 10, color: "#8a8a8a", marginBottom: 8 }}>
        Cuánto de esta factura corresponde a cada OT. Si cubre varias guías de despacho,
        el detalle lo tiene quien despachó — no se deduce de los montos.
      </div>

      {lineas.map((l, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 1fr auto", gap: 6, marginBottom: 5 }}>
          <select style={campo} value={l.workOrderId} onChange={e => set(i, "workOrderId", e.target.value)}>
            <option value="">Elegir OT…</option>
            {ots.map(o => (
              <option key={o.id} value={o.id}>
                OT{o.numero} · {(o.nombre || "").slice(0, 40)}{o.total ? ` · ${clp(o.total)}` : ""}
              </option>
            ))}
          </select>
          <input style={{ ...campo, textAlign: "right" }} type="number" min="0" placeholder="$"
                 value={l.monto} onChange={e => set(i, "monto", e.target.value)} />
          <button type="button" onClick={() => setLineas(ls => ls.filter((_, k) => k !== i))}
                  style={{ background: "none", border: "none", color: "#8a8a8a", cursor: "pointer", fontSize: 14 }}>
            ×
          </button>
        </div>
      ))}

      <button type="button" onClick={() => setLineas(ls => [...ls, { workOrderId: "", monto: "" }])}
              style={{ background: "none", border: "none", color: "#3498db", fontSize: 11, cursor: "pointer", padding: 0 }}>
        + otra OT
      </button>

      {/* El saldo vivo es lo que hace usable esto: se ve al tipear si cuadra, sin apretar
          nada y sin sacar la calculadora. */}
      <div style={{ display: "flex", gap: 12, marginTop: 8, fontSize: 12, flexWrap: "wrap" }}>
        <span style={{ color: "#8a8a8a" }}>Asignado <b style={{ color: "#ddd" }}>{clp(suma)}</b></span>
        <span style={{ color: excede ? "#e74c3c" : Math.abs(resto) <= 10 ? "#2ecc71" : "#f39c12" }}>
          {excede
            ? `se pasa por ${clp(suma - neto)}`
            : Math.abs(resto) <= 10
              ? "cuadra exacto"
              : `faltan ${clp(resto)} por asignar`}
        </span>
      </div>

      {error && <div style={{ fontSize: 11, color: "#e74c3c", marginTop: 6 }}>⚠ {error}</div>}
      {aviso && <div style={{ fontSize: 11, color: "#f39c12", marginTop: 6 }}>{aviso}</div>}

      <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
        <button type="button" onClick={guardar} disabled={guardando || excede}
                style={{ background: "#2ecc7122", color: "#2ecc71", border: "1px solid #2ecc7155",
                         borderRadius: 5, padding: "4px 12px", fontSize: 11, fontWeight: 700,
                         cursor: guardando || excede ? "default" : "pointer", opacity: excede ? .5 : 1 }}>
          {guardando ? "Guardando…" : "Guardar reparto"}
        </button>
        <button type="button" onClick={onCancelar}
                style={{ background: "none", color: "#8a8a8a", border: "1px solid #2e2e2e",
                         borderRadius: 5, padding: "4px 12px", fontSize: 11, cursor: "pointer" }}>
          Cancelar
        </button>
      </div>
    </div>
  );
};

export default RepartoFactura;
